import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TodoPriority } from '../utils/todoPriority';

/**
 * Shared "Att göra nu" list used by both start pages (PersonHome via
 * PersonTodoList, company Home via CompanyTodoList). The pages decide WHICH
 * rows are active and what they say; this component owns the look: one row
 * per item with an icon disc, title, description and one action. Each row
 * has a priority (ClickUp 869fj6nkx) shown as a coloured edge and a text label,
 * so colour is never the only signal: urgent (red, "Brådskande"), waiting
 * (amber, "Väntar på dig") and later (blue, "När du har tid"). The callers
 * sort the rows (sortByPriority).
 */

export interface TodoListItem {
  /** Stable React key. */
  key: string;
  priority: TodoPriority;
  /** Feather icon name without the `feather-` prefix. */
  icon: string;
  /** Already translated. */
  title: string;
  /** Short line under the title that is always shown, e.g. project and contract. */
  context?: string;
  /** The explanation, folded behind "Se mer" so the list stays easy to skim. */
  description: string;
  /** Optional small line under the description (e.g. "a payment is being processed"). */
  note?: string;
  /** Already translated action label. */
  actionLabel: string;
  /** Navigate here on action (rendered as a link)… */
  href?: string;
  /** …or run this (rendered as a button). `href` wins when both are given. */
  onAction?: () => void;
  /** `data-testid` on the row. */
  testId: string;
}

interface TodoListProps {
  items: TodoListItem[];
  /** Rows shown before "Visa fler". */
  maxVisible?: number;
  /** `data-testid` on the empty state. */
  emptyTestId?: string;
}

export default function TodoList({ items, maxVisible = 5, emptyTestId = 'todo-empty' }: TodoListProps) {
  const { t } = useTranslation();
  const [showAll, setShowAll] = useState(false);
  // Each row folds out on its own.
  const [openKeys, setOpenKeys] = useState<Set<string>>(new Set());
  const toggleOpen = (key: string) => setOpenKeys(previous => {
    const next = new Set(previous);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  if (items.length === 0) {
    return (
      <div className="text-center text-muted py-4" data-testid={emptyTestId}>
        <i className="feather-check-circle d-block mb-3 text-success todo-empty-icon" aria-hidden="true"></i>
        <p className="mb-1 fw-semibold">{t('home.todoNow.empty.title')}</p>
        <p className="mb-0 small">{t('home.todoNow.empty.body')}</p>
      </div>
    );
  }

  const hidden = items.length - maxVisible;
  const visibleItems = showAll ? items : items.slice(0, maxVisible);

  return (
    <div>
      <div className="todo-list d-flex flex-column gap-2">
        {visibleItems.map(item => {
          const actionClass = 'btn btn-primary todo-item-action flex-shrink-0';
          const open = openKeys.has(item.key);
          const descriptionId = `todo-desc-${item.key}`;
          return (
            <div
              key={item.key}
              className={`todo-item todo-item--${item.priority}`}
              data-testid={item.testId}
              data-priority={item.priority}
            >
              <div className="d-flex align-items-start gap-3 flex-grow-1 todo-item-text">
                <span className="todo-item-icon flex-shrink-0" aria-hidden="true">
                  <i className={`feather-${item.icon}`}></i>
                </span>
                <div className="flex-grow-1 todo-item-text text-break">
                  <span className={`todo-item-priority is-${item.priority}`}>{t(`home.todoNow.priority.${item.priority}`)}</span>
                  <div className="fw-semibold todo-card-title">{item.title}</div>
                  {item.context && <p className="text-muted mb-0 todo-item-context">{item.context}</p>}
                  {item.note && <p className="text-muted small mb-0 mt-1">{item.note}</p>}
                  {item.description && <>
                    <p id={descriptionId} className="text-muted mb-0 todo-item-description" hidden={!open}>{item.description}</p>
                    <button type="button" className="todo-item-more" aria-expanded={open} aria-controls={descriptionId}
                      data-testid={`todo-see-more-${item.key}`} onClick={() => toggleOpen(item.key)}>
                      {t(open ? 'home.todoNow.seeLess' : 'home.todoNow.seeMore')}
                      <i className={`feather-chevron-${open ? 'up' : 'down'}`} aria-hidden="true"></i>
                    </button>
                  </>}
                </div>
              </div>
              {item.href ? (
                <Link className={actionClass} to={item.href}>{item.actionLabel}</Link>
              ) : (
                <button type="button" className={actionClass} onClick={item.onAction}>{item.actionLabel}</button>
              )}
            </div>
          );
        })}
      </div>

      {hidden > 0 && (
        <button
          type="button"
          className="btn btn-link btn-sm p-0 mt-2"
          onClick={() => setShowAll(v => !v)}
        >
          {showAll ? t('home.todoNow.showLess') : t('home.todoNow.showMore', { count: hidden })}
        </button>
      )}
    </div>
  );
}
